/**
 * apiHelpers – backward-compatible wrappers around the shared httpClient.
 *
 * These functions preserve the legacy `{ success, data, errorResponse }` response
 * envelope so that existing callers (AuthUIActions, RegistrationUIActions,
 * ActionCreator, etc.) continue to work without modification.
 *
 * New code should import `httpClient` directly from `src/api/core` and work
 * with typed domain models via the repository / hooks layers.
 *
 * SOLID:
 *  SRP – each exported function handles exactly one HTTP-verb scenario.
 *  DIP – all HTTP calls delegate to IHttpClient; axios is not imported.
 *  OCP – new helpers can be added alongside existing ones.
 *
 * DRY:
 *  The `wrapRequest` helper centralises success/error response shaping so the
 *  envelope `{ success, data, errorResponse }` is built in exactly one place.
 */

import { httpClient } from '../api/core/http-client-instance';
import { HttpResponseError } from '../api/core/http-error';

// ─── Legacy response envelope ─────────────────────────────────────────────────

export interface ApiSuccessResponse<T = unknown> {
    success: true;
    data: T;
}

export interface ApiErrorResponse {
    success: false;
    /** Human-readable error message. */
    data: string;
    /** Parsed server error body, if available. */
    errorResponse: unknown;
}

export type ApiResponse<T = unknown> = ApiSuccessResponse<T> | ApiErrorResponse;

// ─── Central wrapper ──────────────────────────────────────────────────────────

/**
 * Wraps a httpClient call into the legacy `{ success, data, errorResponse }` shape.
 *
 * Always resolves – never rejects – matching the original helper behaviour so
 * callers that check `response.success` instead of catching errors continue to
 * work correctly.
 */
async function wrapRequest<T>(fn: () => Promise<T>): Promise<ApiResponse<T>> {
    try {
        const data = await fn();
        return { success: true, data };
    } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        const errorResponse = err instanceof HttpResponseError ? err.responseBody : undefined;
        return { success: false, data: error.message, errorResponse };
    }
}

// ─── Exported helpers ─────────────────────────────────────────────────────────

/**
 * Authenticated GET.
 *
 * The Bearer token is attached by the httpClient auth interceptor **when a
 * token exists in LocalStorage**. If no token is present (e.g. the user is
 * not logged in) the request is sent without an Authorization header. Use
 * this helper for endpoints that require authentication.
 *
 * Returns the parsed JSON response body wrapped in the legacy envelope.
 */
export const axiosGet = (url: string): Promise<ApiResponse> =>
    wrapRequest(() => httpClient.get(url).then((r) => r.data));

/**
 * GET for public (unauthenticated) endpoints such as forgot-password triggers.
 *
 * Internally this delegates to the same `httpClient.get()` as `axiosGet`.
 * The distinction is semantic and for call-site intent: this helper is used
 * when the caller explicitly does **not** expect (or need) authentication.
 * Because the auth interceptor only adds a Bearer token when one is already
 * present in storage – which it will not be for unauthenticated flows such as
 * `forgotPassword` – the two helpers are behaviourally equivalent in practice.
 *
 * Kept as a separate export to preserve the existing call sites and to make
 * intent clear at the usage point.
 */
export const axiosGetNoHeader = (url: string): Promise<ApiResponse> =>
    wrapRequest(() => httpClient.get(url).then((r) => r.data));

/**
 * POST with JSON body.
 *
 * @param apiName    Endpoint path (without base URL).
 * @param params     Request body – any serialisable value.
 * @param isLogin    Accepted for backward compatibility. Auth is now handled
 *                   automatically by the httpClient interceptor and this
 *                   parameter no longer alters the outgoing request.
 * @param _contentType  Unused; Content-Type is always `application/json`.
 */
export const axiosPostPromise = (
    apiName: string,
    params: unknown,
    isLogin: boolean,
    _contentType?: string
): Promise<ApiResponse> =>
    wrapRequest(() => httpClient.post(apiName, params).then((r) => r.data));

/**
 * PUT with JSON body.
 *
 * @param apiName   Endpoint path (without base URL).
 * @param params    Request body.
 * @param _isLogin  Accepted for backward compatibility; auth is automatic.
 */
export const axiosPutPromise = (
    apiName: string,
    params: unknown,
    _isLogin: boolean
): Promise<ApiResponse> =>
    wrapRequest(() => httpClient.put(apiName, params).then((r) => r.data));

/**
 * DELETE.
 *
 * @param apiName   Endpoint path (without base URL).
 * @param _isLogin  Accepted for backward compatibility; auth is automatic.
 */
export const axiosDeletePromise = (
    apiName: string,
    _isLogin: boolean
): Promise<ApiResponse> =>
    wrapRequest(() => httpClient.delete(apiName).then((r) => r.data));
